import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { CheckCircle2, RefreshCw, XCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { AppHeader } from "@/components/AppHeader";
import { getPlatformSegment, trackLogoRender, type PlatformSegment } from "@/lib/analytics";
import inquiraLogoUrl from "@/assets/inquira-logo.png";

export const Route = createFileRoute("/icon-diagnostics")({
  component: IconDiagnosticsPage,
  head: () => ({
    meta: [
      { title: "Icon Diagnostics — Inquira" },
      {
        name: "description",
        content:
          "Resolved Android, iOS and PWA icon asset URLs for Inquira with per-asset load results and device/OS segmentation.",
      },
      { property: "og:type", content: "website" },
      { property: "og:title", content: "Icon Diagnostics — Inquira" },
      { property: "og:description", content: "Per-platform icon asset resolution and load results." },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Icon Diagnostics — Inquira" },
      { name: "twitter:description", content: "Per-platform icon asset resolution and load results." },
      { name: "robots", content: "noindex" },
    ],
  }),
});

type Platform = "android" | "ios" | "pwa" | "web";

interface Probe {
  platform: Platform;
  /** Human label, e.g. "PWA maskable 512". */
  label: string;
  /** Web-reachable URL, or null for a native-only resource we can only report. */
  url: string | null;
  /** Declared/expected square size in px, when known. */
  expected?: number;
  note?: string;
}

interface Result extends Probe {
  status: "pending" | "ok" | "failed" | "native-only";
  naturalWidth?: number;
  naturalHeight?: number;
  resolvedUrl?: string;
  reason?: string;
}

/** Native launcher resources are compiled into the Android APK/AAB, so the
 *  WebView cannot fetch them — they are reported by declared resource path. */
const ANDROID_RESOURCES = [
  "res/mipmap-{m,h,xh,xxh,xxx}dpi/ic_launcher.png",
  "res/mipmap-{m,h,xh,xxh,xxx}dpi/ic_launcher_round.png",
  "res/mipmap-{m,h,xh,xxh,xxx}dpi/ic_launcher_foreground.png",
  "res/mipmap-anydpi-v26/ic_launcher.xml (adaptive)",
  "res/values/ic_launcher_background.xml (#FFFFFF)",
];

function buildProbes(): Probe[] {
  const probes: Probe[] = [
    { platform: "web", label: "Header logo (hashed build asset)", url: inquiraLogoUrl },
    { platform: "ios", label: "Apple touch icon 180", url: "/apple-touch-icon.png", expected: 180 },
    { platform: "pwa", label: "Home-screen icon 192", url: "/app-icon-192.png", expected: 192 },
    { platform: "pwa", label: "Home-screen icon 512", url: "/app-icon-512.png", expected: 512 },
    { platform: "pwa", label: "Maskable icon 512", url: "/app-icon-maskable-512.png", expected: 512 },
    { platform: "web", label: "Favicon 32", url: "/favicon-32x32.png", expected: 32 },
  ];
  for (const res of ANDROID_RESOURCES) {
    probes.push({
      platform: "android",
      label: res,
      url: null,
      note: "Compiled into the Android package; verified by `npm run verify:android-icons`.",
    });
  }
  return probes;
}

function probeUrl(probe: Probe): Promise<Result> {
  return new Promise((resolve) => {
    if (!probe.url) {
      resolve({ ...probe, status: "native-only" });
      return;
    }
    const img = new Image();
    const url = probe.url;
    img.onload = () => {
      const sizeMismatch =
        probe.expected !== undefined &&
        (img.naturalWidth !== probe.expected || img.naturalHeight !== probe.expected);
      const result: Result = {
        ...probe,
        status: sizeMismatch ? "failed" : "ok",
        naturalWidth: img.naturalWidth,
        naturalHeight: img.naturalHeight,
        resolvedUrl: img.currentSrc || img.src,
        reason: sizeMismatch
          ? `decoded at ${img.naturalWidth}x${img.naturalHeight}, expected ${probe.expected}`
          : undefined,
      };
      console.info("[inquira-icon] loaded", { requestedUrl: url, ...result });
      trackLogoRender({
        ok: !sizeMismatch,
        src: result.resolvedUrl ?? url,
        natural_width: img.naturalWidth,
        natural_height: img.naturalHeight,
        reason: result.reason ?? `icon:${probe.platform}`,
      });
      resolve(result);
    };
    img.onerror = () => {
      console.error("[inquira-icon] failed to load", { url, platform: probe.platform });
      trackLogoRender({ ok: false, src: url, reason: `icon-unresolvable:${probe.platform}` });
      resolve({ ...probe, status: "failed", reason: "asset URL did not resolve or decode" });
    };
    img.src = url;
  });
}

function StatusIcon({ status }: { status: Result["status"] }) {
  if (status === "ok") return <CheckCircle2 className="size-4 text-neon-green shrink-0" aria-hidden />;
  if (status === "failed") return <XCircle className="size-4 text-destructive shrink-0" aria-hidden />;
  return <span className="size-4 shrink-0 rounded-full border border-rule" aria-hidden />;
}

function IconDiagnosticsPage() {
  const { t } = useTranslation();
  const [results, setResults] = useState<Result[]>(() => buildProbes().map((p) => ({ ...p, status: "pending" })));
  const [segment, setSegment] = useState<PlatformSegment | null>(null);
  const [checkedAt, setCheckedAt] = useState<string | null>(null);
  const [running, setRunning] = useState(false);

  const run = useCallback(async () => {
    setRunning(true);
    setSegment(getPlatformSegment());
    const next = await Promise.all(buildProbes().map(probeUrl));
    setResults(next);
    setCheckedAt(new Date().toLocaleString());
    setRunning(false);
  }, []);

  useEffect(() => {
    void run();
  }, [run]);

  const groups: { platform: Platform; title: string }[] = [
    { platform: "android", title: t("iconDiagnostics.android", "Android launcher icons") },
    { platform: "ios", title: t("iconDiagnostics.ios", "iOS home-screen icon") },
    { platform: "pwa", title: t("iconDiagnostics.pwa", "PWA / installed web app icons") },
    { platform: "web", title: t("iconDiagnostics.web", "Web build assets") },
  ];

  const failures = results.filter((r) => r.status === "failed").length;

  return (
    <div className="min-h-screen bg-bg">
      <AppHeader />
      <main className="mx-auto max-w-2xl px-4 pb-16">
        <h1 className="font-display text-2xl font-black text-ink">
          {t("iconDiagnostics.title", "Icon Diagnostics")}
        </h1>
        <p className="mt-1 text-sm text-ink-muted">
          {t(
            "iconDiagnostics.subtitle",
            "Resolved icon asset URLs for each platform, with load result and device segmentation.",
          )}
        </p>

        <div className="mt-4 flex items-center gap-3">
          <Button onClick={() => void run()} disabled={running} size="sm">
            <RefreshCw className={`mr-2 size-4 ${running ? "animate-spin" : ""}`} aria-hidden />
            {t("iconDiagnostics.recheck", "Re-check icons")}
          </Button>
          <span className="text-xs text-ink-muted">
            {checkedAt
              ? t("iconDiagnostics.lastChecked", "Last checked: {{time}}", { time: checkedAt })
              : t("iconDiagnostics.checking", "Checking…")}
          </span>
        </div>

        <div
          className={`mt-4 rounded-xl border p-3 text-sm font-semibold ${
            failures > 0 ? "border-destructive/50 text-destructive" : "border-rule text-ink"
          }`}
        >
          {failures > 0
            ? t("iconDiagnostics.failing", "{{count}} icon asset(s) failed to load on this device.", {
                count: failures,
              })
            : t("iconDiagnostics.healthy", "All reachable icon assets decoded successfully on this device.")}
        </div>

        {segment && (
          <section className="mt-4 rounded-xl border border-rule bg-card p-3">
            <h2 className="text-xs font-bold uppercase tracking-wide text-ink-muted">
              {t("iconDiagnostics.device", "Device / OS segmentation")}
            </h2>
            <dl className="mt-2 grid grid-cols-2 gap-2 text-xs">
              {(
                [
                  [t("iconDiagnostics.build", "Build"), segment.app_build],
                  [t("iconDiagnostics.os", "OS"), `${segment.os} ${segment.os_version}`.trim()],
                  [t("iconDiagnostics.deviceType", "Device type"), segment.device_type],
                  [
                    t("iconDiagnostics.standalone", "Standalone"),
                    segment.standalone ? t("common.yes", "Yes") : t("common.no", "No"),
                  ],
                ] as const
              ).map(([label, value]) => (
                <div key={label}>
                  <dt className="text-ink-muted">{label}</dt>
                  <dd className="font-mono font-semibold text-ink">{value}</dd>
                </div>
              ))}
            </dl>
          </section>
        )}

        {groups.map(({ platform, title }) => (
          <section key={platform} className="mt-4 rounded-xl border border-rule bg-card p-3">
            <h2 className="text-xs font-bold uppercase tracking-wide text-ink-muted">{title}</h2>
            <ul className="mt-2 space-y-2">
              {results
                .filter((r) => r.platform === platform)
                .map((r) => (
                  <li key={r.label} className="flex items-start gap-2 border-b border-rule/60 pb-2 last:border-0">
                    <StatusIcon status={r.status} />
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-semibold text-ink">{r.label}</p>
                      <p className="break-all font-mono text-[11px] text-ink-muted">
                        {r.resolvedUrl ?? r.url ?? r.note ?? "—"}
                      </p>
                      {r.status === "ok" && r.naturalWidth && (
                        <p className="text-[11px] text-ink-muted">
                          {r.naturalWidth}×{r.naturalHeight}
                        </p>
                      )}
                      {r.reason && <p className="text-[11px] font-semibold text-destructive">{r.reason}</p>}
                    </div>
                  </li>
                ))}
            </ul>
          </section>
        ))}
      </main>
    </div>
  );
}
